import React, { useState, useEffect } from 'react';
import { Calculator, Eye, MousePointer, Percent } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const BannerAdClickThroughRateCalculator = () => {
  const [impressions, setImpressions] = useState(100000);
  const [clicks, setClicks] = useState(500);
  const [ctr, setCtr] = useState(0);

  useEffect(() => {
    calculateCTR();
  }, [impressions, clicks]);

  const calculateCTR = () => {
    const calculatedCtr = (clicks / impressions) * 100;
    setCtr(parseFloat(calculatedCtr.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/banner-ad-click-through-rate-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Banner Ad CTR Calculator: Optimize Click Rates | Free Tool</title>
        <meta name="description" content="Calculate your Banner Ad Click-Through Rate instantly. Compare with industry benchmarks, get optimization tips, and improve ad performance." />
        <meta name="keywords" content="banner ad CTR calculator, click-through rate, display advertising, ad performance metrics, CTR optimization" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Banner Ad CTR Calculator: Optimize Click Rates" />
        <meta property="og:description" content="Calculate your Banner Ad Click-Through Rate instantly. Get actionable insights to improve ad performance and boost clicks." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Banner Ad CTR Calculator: Optimize Click Rates" />
        <meta property="twitter:description" content="Calculate your Banner Ad Click-Through Rate instantly. Get actionable insights to improve ad performance and boost clicks." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Banner Ad Click-Through Rate Calculator",
            "description": "Free calculator to measure and optimize banner ad click-through rates",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate CTR",
              "Industry Benchmarks",
              "Optimization Tips",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Banner Ad Click-Through Rate",
              "description": "The percentage of impressions that resulted in a click on a banner advertisement."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Display Advertisers, Media Buyers, Digital Marketers"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Banner Ad CTR",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter total impressions"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input number of clicks"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your CTR and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Banner Ad Click-Through Rate Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Impressions</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Eye className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={impressions}
                  onChange={(e) => setImpressions(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="100000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Clicks</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MousePointer className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={clicks}
                  onChange={(e) => setClicks(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="500"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {ctr}%
            <Percent className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Banner Ad Click-Through Rate (CTR)</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Banner Ad Click-Through Rate is {ctr}%. This means that {ctr}% of people who saw your banner ad clicked on it.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Format Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Standard Display: 0.05-0.10%</li>
          <li>Rich Media: 0.10-0.15%</li>
          <li>Retargeting: 0.30-0.50%</li>
          <li>Native Ads: 0.20-0.30%</li>
          <li>Mobile Banners: 0.15-0.25%</li>
          <li>Video Display: 0.25-0.35%</li>
          <li>Interstitials: 0.30-0.40%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">CTR Optimization Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Create eye-catching designs</li>
          <li>Use compelling CTAs</li>
          <li>Target relevant audiences</li>
          <li>Test ad placements</li>
          <li>Implement retargeting</li>
          <li>Use interactive elements</li>
          <li>Ensure mobile responsiveness</li>
          <li>A/B test variations</li>
          <li>Optimize ad sizes</li>
          <li>Monitor frequency caps</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Design Best Practices</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Clear value proposition</li>
          <li>Strong contrast</li>
          <li>Brand consistency</li>
          <li>Simple messaging</li>
          <li>Quality imagery</li>
          <li>Proper spacing</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact Analysis</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Strong CTA: +25% CTR</li>
          <li>Retargeting: +200% CTR</li>
          <li>Proper targeting: +50% CTR</li>
          <li>A/B testing: +30% CTR</li>
          <li>Interactive elements: +40% CTR</li>
        </ul>
      </div>
    </div>
  );
};

export default BannerAdClickThroughRateCalculator;
